import type { PaginationResponse } from '@/types/api';
import type { Barang, Satuan, Kategori, SupplierRef, ImportBarangItem, ImportBarangResult } from '@/types/barang';
import type { ImportSupplierItem, ImportSupplierResult } from '@/types/supplier';
import type { ImportKategoriBarangItem, ImportKategoriBarangResult } from '@/types/kategoriBarang';
import type { ImportDaftarKendaraanItem, ImportDaftarKendaraanResult } from '@/types/daftarKendaraan';
import type { Booking, DailySummary } from '@/types/booking';
import type { Layanan, LayananCabangRow, ImportLayananItem, ImportLayananResult } from '@/types/layanan';
import type { Transaksi, LayananData, MekanikData } from '@/types/transaksi';
import type { AkunKas, KategoriKas, MutasiKas, ArusKasSummary, Pembayaran } from '@/types/kas';
import { isPublicPath } from '@/lib/marketingRoutes';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || '/api';

interface ApiResponse<T = any> {
  status: boolean;
  message: string;
  data?: T;
  errors?: Record<string, string[]>;
  // Beberapa endpoint backend menyertakan metadata di level atas response.
  pagination?: any;
  search_info?: any;
}

// Satu pilihan perusahaan (membership aktif) milik user — dipakai untuk
// pemilihan & perpindahan tenant (multi-aktif).
export interface TenantOption {
  perusahaanId: number;
  namaPerusahaan: string;
  role?: string;
  statusPlatform?: string;
}

// Baca cookie dengan nama tertentu (dipakai untuk CSRF token).
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp('(?:^|;\\s*)' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : null;
}

const NON_GET_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// ─── SuperAdmin tenant override (module scope) ─────────────────────────────
// Dipersist ke localStorage agar bertahan melewati window.location.reload()
// yang dipicu tenant switcher SuperAdmin/Owner di Sidebar.
const SA_OVERRIDE_KEY = 'superadmin_tenant_override';

function readSuperAdminOverride(): { perusahaanId: number | null; cabangId: number | null } {
  if (typeof window === 'undefined') return { perusahaanId: null, cabangId: null };
  try {
    const raw = window.localStorage.getItem(SA_OVERRIDE_KEY);
    if (!raw) return { perusahaanId: null, cabangId: null };
    const parsed = JSON.parse(raw);
    return {
      perusahaanId: typeof parsed?.perusahaanId === 'number' ? parsed.perusahaanId : null,
      cabangId: typeof parsed?.cabangId === 'number' ? parsed.cabangId : null,
    };
  } catch {
    return { perusahaanId: null, cabangId: null };
  }
}

const _initialOverride = readSuperAdminOverride();
let _superAdminPerusahaanId: number | null = _initialOverride.perusahaanId;
let _superAdminCabangId: number | null = _initialOverride.cabangId;

class ApiClient {
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;

  private buildHeaders(method: string): HeadersInit {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    // CSRF: double-submit cookie pattern. Browser kirim cookie XSRF-TOKEN
    // otomatis, kita echo nilainya di header X-XSRF-TOKEN untuk non-GET.
    if (NON_GET_METHODS.has(method.toUpperCase())) {
      const csrf = getCookie('XSRF-TOKEN');
      if (csrf) headers['X-XSRF-TOKEN'] = csrf;
    }
    return headers;
  }

  private async refreshAccessToken(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: this.buildHeaders('POST'),
      });
      return response.ok;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  private async handleResponse<T>(response: Response, originalRequest?: () => Promise<Response>): Promise<ApiResponse<T>> {
    // 401 → coba silent refresh sekali, lalu retry request asli.
    if (response.status === 401 && originalRequest) {
      let refreshed: boolean;
      if (this.isRefreshing && this.refreshPromise) {
        refreshed = await this.refreshPromise;
      } else {
        this.isRefreshing = true;
        this.refreshPromise = this.refreshAccessToken();
        refreshed = await this.refreshPromise;
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
      if (refreshed) {
        const retryResponse = await originalRequest();
        return this.handleResponse<T>(retryResponse);
      }
      // Refresh gagal → cookie sudah expired/tidak valid → redirect ke login.
      // Kecuali halaman publik (landing, marketing, blog, auth): pengunjung
      // anonim harus tetap bisa membacanya. Daftarnya di lib/marketingRoutes.ts.
      if (typeof window !== 'undefined' && !isPublicPath(window.location.pathname)) {
        window.location.href = '/login';
      }
    }

    // Baca sebagai text dulu untuk handle response kosong / non-JSON
    const text = await response.text();
    let parsed: any = null;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text;
      }
    }

    if (!response.ok) {
      const message = (parsed && typeof parsed === 'object' && parsed.message)
        || (typeof parsed === 'string' && parsed)
        || response.statusText
        || 'Something went wrong';
      const errText = `${response.status} ${message}`;
      throw new Error(errText);
    }

    if (parsed && typeof parsed === 'object') {
      return parsed as ApiResponse<T>;
    }

    return {
      status: true,
      message: typeof parsed === 'string' ? parsed : (response.statusText || 'OK'),
    } as ApiResponse<T>;
  }

  private async request<T = any>(method: string, endpoint: string, body?: any): Promise<ApiResponse<T>> {
    // SuperAdmin tenant override (?perusahaanId=X[&cabangId=Y]) berlaku untuk SEMUA
    // method — bukan hanya GET — agar operasi write (POST/PUT/DELETE/PATCH) ikut
    // ter-scope ke perusahaan yang sedang dipilih SuperAdmin. No-op bila override null.
    const url = _superAdminPerusahaanId ? appendSuperAdminParams(endpoint) : endpoint;
    const init: RequestInit = {
      method,
      credentials: 'include',
      headers: this.buildHeaders(method),
    };
    if (body !== undefined) {
      init.body = JSON.stringify(body);
    }
    const makeRequest = () => fetch(`${API_BASE_URL}${url}`, init);
    const response = await makeRequest();
    return this.handleResponse<T>(response, makeRequest);
  }

  async get<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>('GET', endpoint);
  }

  async post<T = any>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>('POST', endpoint, data);
  }

  async put<T = any>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', endpoint, data);
  }

  async delete<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', endpoint);
  }

  async patch<T = any>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', endpoint, data);
  }
}

export const apiClient = new ApiClient();

// ─── SuperAdmin tenant override ────────────────────────────────────────────
export function setSuperAdminTenantOverride(
  perusahaanId: number | null,
  cabangId: number | null,
) {
  _superAdminPerusahaanId = perusahaanId;
  _superAdminCabangId = cabangId;
  if (typeof window !== 'undefined') {
    try {
      if (perusahaanId != null) {
        window.localStorage.setItem(SA_OVERRIDE_KEY, JSON.stringify({ perusahaanId, cabangId }));
      } else {
        window.localStorage.removeItem(SA_OVERRIDE_KEY);
      }
    } catch { /* localStorage unavailable — abaikan */ }
  }
}

const SA_SELECTION_KEY = 'superadmin_tenant_selection';

/** Hapus semua persistensi tenant switcher (dipakai saat logout). */
export function clearSuperAdminTenantPersistence() {
  setSuperAdminTenantOverride(null, null);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(SA_SELECTION_KEY);
    } catch { /* localStorage unavailable — abaikan */ }
  }
}

/** Tambahkan ?perusahaanId=X[&cabangId=Y] ke URL bila SuperAdmin override aktif. */
export function appendSuperAdminParams(url: string): string {
  if (!_superAdminPerusahaanId) return url;
  const sep = url.includes('?') ? '&' : '?';
  let result = `${url}${sep}perusahaanId=${_superAdminPerusahaanId}`;
  if (_superAdminCabangId) result += `&cabangId=${_superAdminCabangId}`;
  return result;
}

// Auth API
// Note: token sekarang diset/dibaca dari HttpOnly cookie oleh backend.
// Response body hanya berisi data user; tidak lagi memuat access_token / refresh_token.
export const authApi = {
  register: (data: {
    namaBengkel: string;
    nama: string;
    email: string;
    password: string;
    noTelepon: string;
    alamat?: string;
  }) => apiClient.post('/auth/register', data),

  login: (data: { email: string; password: string }) =>
    apiClient.post<any>('/auth/login', data),

  // Restore user state via HttpOnly cookie — dipanggil saat AuthContext mount.
  me: () => apiClient.get('/auth/me'),

  // Daftar perusahaan (membership aktif) milik user untuk pemilihan/switch tenant.
  myPerusahaan: () => apiClient.get<TenantOption[]>('/auth/my-perusahaan'),

  // Memilih / berpindah perusahaan aktif sesi. Backend menerbitkan ulang cookie token.
  selectTenant: (perusahaanId: number) =>
    apiClient.post<any>('/auth/select-tenant', { perusahaanId }),

  // Refresh token dibaca dari HttpOnly cookie REFRESH_TOKEN oleh backend.
  refresh: () => apiClient.post('/auth/refresh', {}),

  logout: () => apiClient.post('/auth/logout', {}),

  googleAuth: (idToken: string) =>
    apiClient.post<any>('/auth/google-auth', { idToken }),

  forgotPassword: (email: string) =>
    apiClient.post('/auth/forgot-password', { email }),

  resetPassword: (token: string, newPassword: string) =>
    apiClient.post('/auth/reset-password', { token, newPassword }),

  verifyEmail: (token: string) =>
    apiClient.post('/auth/verify-email', { token }),

  // 2FA Google Authenticator (TOTP) — SuperAdmin.
  totp: {
    // Mulai enrollment: balikan { qrDataUri, secret, otpauthUrl }.
    setup: () => apiClient.post<any>('/auth/totp/setup', {}),
    // Aktifkan 2FA dengan kode pertama dari authenticator.
    activate: (code: string) => apiClient.post('/auth/totp/activate', { code }),
    // Nonaktifkan 2FA (butuh kode saat ini).
    disable: (code: string) => apiClient.post('/auth/totp/disable', { code }),
    // Gate login: verifikasi kode → backend terbitkan ulang cookie (mfa=true).
    verify: (code: string) => apiClient.post<any>('/auth/totp/verify', { code }),
    // Recovery: minta link reset 2FA ke email (saat HP authenticator hilang).
    resetRequest: () => apiClient.post('/auth/totp/reset/request', {}),
    // Konfirmasi reset via token dari email → 2FA dinonaktifkan.
    resetConfirm: (token: string) => apiClient.post('/auth/totp/reset/confirm', { token }),
  },

  // Verifikasi email via kode OTP (dikirim ke email user setelah login).
  emailOtp: {
    // Kirim / kirim ulang kode 6 digit ke email user.
    send: () => apiClient.post('/auth/email-otp/send', {}),
    // Verifikasi kode → backend set emailVerified=true, balikan UserResponse terbaru.
    verify: (code: string) => apiClient.post<any>('/auth/email-otp/verify', { code }),
  },
};

// Onboarding API
export const onboardingApi = {
  getStatus: () => apiClient.get('/onboarding/status'),

  saveProfil: (data: { namaPerusahaan: string; telepon: string; alamat: string }) =>
    apiClient.put('/onboarding/profil', data),

  saveCabang: (data: { namaCabang: string; alamat?: string; telepon?: string }) =>
    apiClient.put('/onboarding/cabang', data),

  addUser: (data: { email: string; password: string; nama: string; noTelepon?: string; roleId: number }) =>
    apiClient.post('/onboarding/user', data),

  addMekanik: (data: { nama: string; email: string; password: string; noHp?: string; spesialisasi?: string }) =>
    apiClient.post('/onboarding/mekanik', data),

  addLayanan: (data: { namaLayanan: string; hargaLayanan: number; durasi: number }) =>
    apiClient.post('/onboarding/layanan', data),

  complete: () => apiClient.post('/onboarding/complete', {}),

  resendVerification: () => apiClient.post('/onboarding/resend-verification', {}),
};

// Merchant API
export const merchantApi = {
  getProfile: () => apiClient.get('/merchant/profile'),
  updateProfile: (data: any) => apiClient.put('/merchant/profile', data),
};

// Pelanggan API
export const pelangganApi = {
  getAll: (params?: { page?: string; limit?: string; search?: string; search_type?: string; status?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page);
    if (params?.limit) queryParams.append('limit', params.limit);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    const queryString = queryParams.toString();
    return apiClient.get(`/pelanggan${queryString ? `?${queryString}` : ''}`);
  },
  getById: (id: number) => apiClient.get(`/pelanggan/${id}`),
  getHistoriService: (
    id: string | number,
    params?: { page?: number; perPage?: number; search?: string; startDate?: string; endDate?: string }
  ) => {
    const queryParams = new URLSearchParams();
    queryParams.append('id', String(id));
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    return apiClient.get(`/pelanggan/histori-service?${queryParams.toString()}`);
  },
  getHistoriServiceDetail: (transaksiId: string | number) =>
    apiClient.get(`/pelanggan/histori-service/detail/${transaksiId}`),
  create: (data: any) => apiClient.post('/pelanggan', data),
  update: (id: string | number, data: any) => apiClient.put(`/pelanggan/${id}`, data),
  delete: (id: string | number) => apiClient.delete(`/pelanggan/${id}`),
  activate: (id: string | number) => apiClient.patch(`/pelanggan/${id}/activate`, {}),
  // Deteksi identitas pelanggan global (lintas perusahaan) berdasarkan email/no_hp
  lookup: (params: { email?: string; noHp?: string }) => {
    const queryParams = new URLSearchParams();
    if (params.email) queryParams.append('email', params.email);
    if (params.noHp) queryParams.append('noHp', params.noHp);
    return apiClient.get(`/pelanggan/lookup?${queryParams.toString()}`);
  },
  // Tautkan pelanggan global yang sudah ada ke perusahaan aktif
  link: (pelangganId: string | number) =>
    apiClient.post('/pelanggan/link', { pelanggan_id: Number(pelangganId) }),
};

// Daftar Kendaraan API
export const daftarKendaraanApi = {
  getAll: (params?: { page?: number; perPage?: number; search?: string; status?: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    return apiClient.get(`/daftar-kendaraan?${queryParams.toString()}`);
  },
  create: (data: { merk: string; tipe: string }) =>
    apiClient.post('/daftar-kendaraan', data),
  /**
   * Impor massal kendaraan. Selalu membalas 200 dengan laporan per baris — baris yang
   * gagal tidak membatalkan baris lain (tiap baris = transaksi terpisah di backend).
   * Satu request = satu token rate-limit tulis, jadi JANGAN diganti loop create().
   */
  importBulk: (data: { items: ImportDaftarKendaraanItem[] }) =>
    apiClient.post<ImportDaftarKendaraanResult>('/daftar-kendaraan/bulk', data),
  update: (data: { id: number; merk: string; tipe: string }) =>
    apiClient.post('/daftar-kendaraan/update-kendaraan', data),
  delete: (id: number) => apiClient.delete(`/daftar-kendaraan/${id}`),
};

// Kendaraan per Pelanggan API
export const kendaraanApi = {
  add: (pelangganId: string | number, data: {
    tahun: number;
    warna: string;
    daftar_kendaraan_id: number;
    nomor_polisi: string;
    nomor_rangka: string;
    nomor_mesin: string;
    kilometer_terakhir: number;
  }) => apiClient.post(`/pelanggan/${pelangganId}/kendaraan`, data),

  update: (pelangganId: string | number, kendaraanId: string | number, data: {
    tahun: number;
    warna: string;
    daftar_kendaraan_id: number;
    nomor_polisi: string;
    nomor_rangka: string;
    nomor_mesin: string;
    kilometer_terakhir: number;
  }) => apiClient.put(`/pelanggan/${pelangganId}/kendaraan/${kendaraanId}`, data),

  delete: (pelangganId: string | number, kendaraanId: string | number) =>
    apiClient.delete(`/pelanggan/${pelangganId}/kendaraan/${kendaraanId}`),

  activate: (pelangganId: string | number, kendaraanId: string | number) =>
    apiClient.patch(`/pelanggan/${pelangganId}/kendaraan/${kendaraanId}/activate`, {}),
};

// Layanan API
export const layananApi = {
  getAll: (params?: { page?: number; perPage?: number; search?: string; status?: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    queryParams.append('search', params?.search ?? '');
    queryParams.append('status', params?.status ?? '');
    return apiClient.get<PaginationResponse<Layanan>>(`/layanan?${queryParams.toString()}`);
  },
  getAllMaster: (params?: { page?: number; perPage?: number; search?: string; status?: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    queryParams.append('search', params?.search ?? '');
    queryParams.append('status', params?.status ?? '');
    return apiClient.get<PaginationResponse<Layanan>>(`/layanan/master?${queryParams.toString()}`);
  },
  getById: (id: number) => apiClient.get<Layanan>(`/layanan/${id}`),
  create: (data: {
    namaLayanan: string;
    durasi: number;
    hargaLayanan: number;
    statusLayanan: string;
    namaOverride?: string | null;
    hargaLayananOverride?: number | null;
    durasiOverride?: number | null;
    isPublish?: boolean;
  }) => apiClient.post('/layanan', data),
  /**
   * Impor massal layanan. Selalu membalas 200 dengan laporan per baris — baris yang
   * gagal tidak membatalkan baris lain (tiap baris = transaksi terpisah di backend).
   * Satu request = satu token rate-limit tulis, jadi JANGAN diganti loop create().
   */
  importBulk: (data: { items: ImportLayananItem[] }) =>
    apiClient.post<ImportLayananResult>('/layanan/bulk', data),
  update: (data: {
    id: number;
    namaLayanan: string;
    durasi: number;
    hargaLayanan: number;
    statusLayanan: string;
    namaOverride?: string | null;
    hargaLayananOverride?: number | null;
    durasiOverride?: number | null;
    isPublish?: boolean;
  }) => apiClient.post('/layanan/update-layanan', data),
  updateMaster: (data: {
    id: number;
    namaLayanan: string;
    durasi: number;
    hargaLayanan: number;
    statusLayanan: string;
  }) => apiClient.post('/layanan/master/update', data),
  delete: (id: number) => apiClient.delete(`/layanan/${id}`),
  activate: (id: number) => apiClient.post(`/layanan/${id}/aktifkan`, {}),
  getLayananCabang: (id: number) => apiClient.get<LayananCabangRow[]>(`/layanan/${id}/layanan-cabang`),
  upsertLayananCabang: (id: number, data: {
    cabangId: number;
    namaOverride?: string | null;
    hargaLayananOverride?: number | null;
    durasiOverride?: number | null;
    status?: string;
    isPublish?: boolean;
  }) => apiClient.post(`/layanan/${id}/layanan-cabang`, data),
  deleteLayananCabang: (id: number, cabangId: number) =>
    apiClient.delete(`/layanan/${id}/layanan-cabang/${cabangId}`),
  promote: (transaksiLayananId: number) => apiClient.post(`/layanan/promote/${transaksiLayananId}`, {}),
};

// Riwayat baris layanan transaksi (transaksi_layanan)
export interface TransaksiLayananHistory {
  id: number;
  transaksiId: number | null;
  kodeTransaksi: string | null;
  tanggalServis: string | null;
  namaLayanan: string;
  manual: boolean;
  hargaLayanan: number;
  durasi: number | null;
  mekanikNama: string | null;
  statusServis: string | null;
  keterangan: string | null;
  createdAt: string;
}

export const transaksiLayananApi = {
  getHistory: (params?: { page?: number; perPage?: number; search?: string; manual?: boolean }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    queryParams.append('search', params?.search ?? '');
    queryParams.append('manual', (params?.manual ?? false).toString());
    return apiClient.get<PaginationResponse<TransaksiLayananHistory>>(`/transaksi-layanan/history?${queryParams.toString()}`);
  },
};

// Promo API — promo engine generik (target/rule/benefit)
export interface PromoPayload {
  id?: number;
  kode?: string | null;
  nama: string;
  deskripsi?: string | null;
  promoKind: string;
  cabangId?: number | null;
  tanggalMulai: string; // ISO LocalDateTime, mis. 2026-06-01T00:00:00
  tanggalSelesai: string;
  kuota?: number | null;
  status: string;
  targets: Array<{ targetType: string; targetId?: number | null }>;
  rules: Array<{ ruleType: string; operator?: string | null; ruleValue?: string | null }>;
  benefits: Array<{ benefitType: string; benefitValue?: number | null; targetType?: string | null; targetId?: number | null }>;
}

export const promoApi = {
  getAll: (params?: { page?: number; perPage?: number; search?: string; status?: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    queryParams.append('search', params?.search ?? '');
    queryParams.append('status', params?.status ?? '');
    return apiClient.get(`/promo?${queryParams.toString()}`);
  },
  getById: (id: number) => apiClient.get(`/promo/${id}`),
  create: (data: PromoPayload) => apiClient.post('/promo', data),
  update: (data: PromoPayload) => apiClient.post('/promo/update-promo', data),
  delete: (id: number) => apiClient.delete(`/promo/${id}`),
  publish: (id: number, data: { title?: string; message?: string }) =>
    apiClient.post(`/promo/${id}/publish`, data),
  evaluasi: (data: {
    pelangganId?: number | null;
    kode?: string | null;
    items: Array<{ targetType: string; refId?: number | null; qty: number; harga: number }>;
  }) => apiClient.post('/promo/evaluasi', data),
  applyTransaksi: (transaksiId: number, promoId: number) =>
    apiClient.post(`/promo/apply/transaksi/${transaksiId}/${promoId}`, {}),
  applyPenjualan: (penjualanId: number, promoId: number) =>
    apiClient.post(`/promo/apply/penjualan/${penjualanId}/${promoId}`, {}),
  applyTransaksiByKode: (transaksiId: number, kode: string) =>
    apiClient.post(`/promo/apply-kode/transaksi/${transaksiId}`, { kode }),
  applyPenjualanByKode: (penjualanId: number, kode: string) =>
    apiClient.post(`/promo/apply-kode/penjualan/${penjualanId}`, { kode }),
  removeFromTransaksi: (transaksiId: number) =>
    apiClient.delete(`/promo/transaksi/${transaksiId}`),
  removeFromPenjualan: (penjualanId: number) =>
    apiClient.delete(`/promo/penjualan/${penjualanId}`),
};

// Feedback API (admin) — feedback pelanggan ter-agregat per transaksi
export const feedbackApi = {
  getAll: (params?: { page?: number; perPage?: number; search?: string; status?: string; kategori?: string; cabangId?: number | null }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    queryParams.append('search', params?.search ?? '');
    queryParams.append('status', params?.status ?? '');
    queryParams.append('kategori', params?.kategori ?? '');
    if (params?.cabangId != null) queryParams.append('cabangId', params.cabangId.toString());
    return apiClient.get(`/feedback?${queryParams.toString()}`);
  },
  // Laporan feedback per cabang (filter bintang & tanggal, sort terbaru/terlama)
  report: (params?: {
    page?: number;
    perPage?: number;
    cabangId?: number | null;
    bintang?: number | null;
    startDate?: string | null;
    endDate?: string | null;
    sort?: 'terbaru' | 'terlama';
  }) => {
    const q = new URLSearchParams();
    q.append('page', (params?.page ?? 1).toString());
    q.append('perPage', (params?.perPage ?? 10).toString());
    if (params?.cabangId != null) q.append('cabangId', params.cabangId.toString());
    if (params?.bintang != null) q.append('bintang', params.bintang.toString());
    if (params?.startDate) q.append('startDate', params.startDate);
    if (params?.endDate) q.append('endDate', params.endDate);
    if (params?.sort) q.append('sort', params.sort);
    return apiClient.get(`/feedback/report?${q.toString()}`);
  },
  reportSummary: (params?: { cabangId?: number | null; startDate?: string | null; endDate?: string | null }) => {
    const q = new URLSearchParams();
    if (params?.cabangId != null) q.append('cabangId', params.cabangId.toString());
    if (params?.startDate) q.append('startDate', params.startDate);
    if (params?.endDate) q.append('endDate', params.endDate);
    const qs = q.toString();
    return apiClient.get(`/feedback/report/summary${qs ? `?${qs}` : ''}`);
  },
  getByTransaksi: (transaksiId: number) => apiClient.get(`/feedback/${transaksiId}`),
  updateStatus: (transaksiId: number, status: string) =>
    apiClient.put(`/feedback/${transaksiId}/status`, { status }),
  reply: (transaksiId: number, balasan: string) =>
    apiClient.put(`/feedback/${transaksiId}/reply`, { balasan }),
  delete: (transaksiId: number) => apiClient.delete(`/feedback/${transaksiId}`),
};

// Kategori Barang API
export const kategoriBarangApi = {
  getAll: (params?: { page?: number; perPage?: number; search?: string; status?: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    return apiClient.get<PaginationResponse<Kategori>>(`/kategori-barang?${queryParams.toString()}`);
  },
  create: (data: { nama: string; status?: string }) =>
    apiClient.post('/kategori-barang', data),
  /**
   * Impor massal kategori barang. Selalu membalas 200 dengan laporan per baris — baris
   * yang gagal tidak membatalkan baris lain (tiap baris = transaksi terpisah di backend).
   * Satu request = satu token rate-limit tulis, jadi JANGAN diganti loop create().
   */
  importBulk: (data: { items: ImportKategoriBarangItem[] }) =>
    apiClient.post<ImportKategoriBarangResult>('/kategori-barang/bulk', data),
  update: (data: { id: number; nama: string; status?: string }) =>
    apiClient.post('/kategori-barang/update-kategori', data),
  delete: (id: number) => apiClient.delete(`/kategori-barang/${id}`),
};

// Supplier API
export const supplierApi = {
  getAll: (params?: { page?: number; perPage?: number; search?: string; status?: string; startDate?: string; endDate?: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    return apiClient.get<PaginationResponse<SupplierRef>>(`/supplier?${queryParams.toString()}`);
  },
  create: (data: {
    namaSupplier: string;
    noHp?: string;
    email?: string;
    alamat: string;
    namaKontak?: string;
  }) => apiClient.post('/supplier', data),
  /**
   * Impor massal supplier. Selalu membalas 200 dengan laporan per baris — baris yang
   * gagal tidak membatalkan baris lain (tiap baris = transaksi terpisah di backend).
   * Satu request = satu token rate-limit tulis, jadi JANGAN diganti loop create().
   */
  importBulk: (data: { items: ImportSupplierItem[] }) =>
    apiClient.post<ImportSupplierResult>('/supplier/bulk', data),
  update: (data: {
    id: number;
    namaSupplier: string;
    noHp?: string;
    email?: string;
    alamat: string;
    namaKontak?: string;
    status?: string;
  }) => apiClient.post('/supplier/update-supplier', data),
  delete: (id: number) => apiClient.delete(`/supplier/${id}`),
};

// Barang API
export const barangApi = {
  getAll: (params?: { page?: number; perPage?: number; search?: string; status?: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    queryParams.append('search', params?.search ?? '');
    queryParams.append('status', params?.status ?? '');
    return apiClient.get<PaginationResponse<Barang>>(`/barang?${queryParams.toString()}`);
  },
  // Master barang (lintas cabang): semua master per-perusahaan tanpa filter status stok cabang.
  getAllMaster: (params?: { page?: number; perPage?: number; search?: string; status?: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    queryParams.append('search', params?.search ?? '');
    queryParams.append('status', params?.status ?? '');
    return apiClient.get<PaginationResponse<Barang>>(`/barang/master?${queryParams.toString()}`);
  },
  getById: (id: number) => apiClient.get<Barang>(`/barang/${id}`),
  getByBarcode: (barcode: string) =>
    apiClient.get<Barang>(`/barang/by-barcode?barcode=${encodeURIComponent(barcode)}`),
  lookupBarcode: (barcode: string) =>
    apiClient.get<{ exists: boolean; barangId?: number; kodeBarang?: string; namaBarangMaster?: string; stockedInThisCabang?: boolean }>(
      `/barang/lookup-barcode?barcode=${encodeURIComponent(barcode)}`),
  create: (data: {
    namaBarang: string;
    kategori: string;
    satuanId: number;
    stok: number;
    stokMinimum: number;
    hargaBeli: number;
    hargaJual: number;
    supplier?: string;
    deskripsi?: string;
    lokasiRak?: string;
    status: string;
    barcodes?: string[];
    hargaJualOverride?: number | null;
    hargaBeliOverride?: number | null;
  }) => apiClient.post('/barang', data),
  /**
   * Impor massal barang. Selalu membalas 200 dengan laporan per baris — baris yang
   * gagal tidak membatalkan baris lain (tiap baris = transaksi terpisah di backend).
   * Satu request = satu token rate-limit tulis, jadi JANGAN diganti loop create().
   */
  importBulk: (data: { items: ImportBarangItem[] }) =>
    apiClient.post<ImportBarangResult>('/barang/bulk', data),
  update: (data: {
    id: number;
    namaBarang: string;
    kategori: string;
    satuanId: number;
    stok: number;
    stokMinimum: number;
    hargaBeli: number;
    hargaJual: number;
    supplier?: string;
    deskripsi?: string;
    lokasiRak?: string;
    status: string;
    barcodes?: string[];
    hargaJualOverride?: number | null;
    hargaBeliOverride?: number | null;
  }) => apiClient.post('/barang/update-barang', data),
  // Update MASTER saja (tidak menyentuh stok/override cabang) — halaman Master Barang.
  updateMaster: (data: {
    id: number;
    namaBarang: string;
    kategoriId?: number;
    satuanId: number;
    hargaBeli: number;
    hargaJual: number;
    supplierId?: number;
    deskripsi?: string;
    status: string;
    isReminder?: boolean;
    intervalKm?: number | null;
    intervalBulan?: number | null;
    barcodes?: string[];
  }) => apiClient.post('/barang/master/update', data),
  delete: (id: number) => apiClient.delete(`/barang/${id}`),
  // Aktifkan kembali barang di cabang aktif (kebalikan dari delete/nonaktifkan).
  activate: (id: number) => apiClient.post(`/barang/${id}/aktifkan`, {}),
  // Stok per cabang (Fase 2)
  getStokCabang: (id: number) => apiClient.get(`/barang/${id}/stok-cabang`),
  upsertStokCabang: (id: number, data: {
    cabangId: number;
    stok?: number;
    stokMinimum?: number;
    lokasiRak?: string;
    namaOverride?: string | null;
    hargaJualOverride?: number | null;
    hargaBeliOverride?: number | null;
    status?: string;
  }) => apiClient.post(`/barang/${id}/stok-cabang`, data),
  deleteStokCabang: (id: number, cabangId: number) =>
    apiClient.delete(`/barang/${id}/stok-cabang/${cabangId}`),
  addStock: (id: number, data: {
    jumlah: number;
    harga_beli: number;
    supplier: string;
    tanggal_masuk: string;
    keterangan?: string;
  }) => {
    return apiClient.post(`/barang/${id}/stok`, {
      jumlah: data.jumlah,
      harga_beli: data.harga_beli,
      supplier: data.supplier,
      tanggal_masuk: data.tanggal_masuk,
      keterangan: data.keterangan || ''
    });
  },
  getStokMinimum: () => apiClient.get('/barang/stok-minimum'),
};

// Stok API
export const stokApi = {
  getStokMasuk: (params?: {
    limit?: string;
    offset?: string;
    id_barang?: string;
    tanggal_dari?: string;
    tanggal_sampai?: string;
    supplier?: string;
  }) => {
    const queryParams = params ? `?${new URLSearchParams(params).toString()}` : '';
    return apiClient.get(`/stok/masuk${queryParams}`);
  },
  getStokKeluar: (params?: {
    limit?: string;
    offset?: string;
    id_barang?: string;
    tanggal_dari?: string;
    tanggal_sampai?: string;
    id_transaksi?: string;
  }) => {
    const queryParams = params ? `?${new URLSearchParams(params).toString()}` : '';
    return apiClient.get(`/stok/keluar${queryParams}`);
  },
  cancelStokMasuk: (id: number) => {
    return apiClient.put(`/stok/masuk/cancel/${id}`, {});
  },
};

// Stok Log API
export const stokLogApi = {
  getAll: (params?: {
    page?: number;
    perPage?: number;
    kodeBarang?: string;
    tipe?: string;
    referensiTipe?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    if (params?.kodeBarang) queryParams.append('kodeBarang', params.kodeBarang);
    if (params?.tipe) queryParams.append('tipe', params.tipe);
    if (params?.referensiTipe) queryParams.append('referensiTipe', params.referensiTipe);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    return apiClient.get(`/stok-log?${queryParams.toString()}`);
  },
};

// Transaksi Barang API (detail barang per transaksi / barang keluar via transaksi)
export const transaksiBarangApi = {
  getAll: (params?: {
    page?: number;
    perPage?: number;
    idBarang?: number;
    idTransaksi?: number;
    search?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const qp = new URLSearchParams();
    qp.append('page', (params?.page ?? 1).toString());
    qp.append('perPage', (params?.perPage ?? 10).toString());
    if (params?.idBarang) qp.append('idBarang', String(params.idBarang));
    if (params?.idTransaksi) qp.append('idTransaksi', String(params.idTransaksi));
    if (params?.search) qp.append('search', params.search);
    if (params?.startDate) qp.append('startDate', params.startDate);
    if (params?.endDate) qp.append('endDate', params.endDate);
    return apiClient.get(`/transaksi-barang?${qp.toString()}`);
  },
};

// Mekanik API
export const mekanikApi = {
  // Mekanik kini dikelola sebagai user ber-role Mekanik (lihat userApi.cabang).
  // Endpoint ini hanya menyediakan daftar mekanik aktif pada cabang aktif sesi
  // untuk dropdown transaksi service.
  getByCabang: () => apiClient.get<MekanikData[]>('/mekanik/by-cabang'),
};

// Kuota Antrian API
export const kuotaApi = {
  getAll: () => apiClient.get('/kuota'),
  create: (data: { cabangId: number; kuotaOnline: number; kuotaWalkin: number; forceWalkin?: boolean }) =>
    apiClient.post('/kuota', data),
  update: (data: { id: number; kuotaOnline: number; kuotaWalkin: number; forceWalkin?: boolean }) =>
    apiClient.post('/kuota/update-kuota', data),
  delete: (id: number) => apiClient.delete(`/kuota/${id}`),
};

// Antrian API
export const antrianApi = {
  getAll: (params?: {
    page?: number;
    perPage?: number;
    search?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    return apiClient.get(`/antrian?${queryParams.toString()}`);
  },
  create: (data: { pelangganId: number; statusAntrian?: string }) =>
    apiClient.post('/antrian', data),
  update: (data: { id: number; pelangganId: number; statusAntrian?: string; loketId?: number }) =>
    apiClient.post('/antrian/update-antrian', data),
  delete: (id: number) => apiClient.delete(`/antrian/${id}`),
  getQueueInfo: (id: number) => apiClient.get(`/antrian/${id}/queue-info`),
  recall: (id: number) => apiClient.post(`/antrian/${id}/recall`, {}),
};

// Cabang API
// User Management API
type UserCreatePayload = {
  email: string;
  password: string;
  nama: string;
  noTelepon?: string;
  alamat?: string;
  roleId?: number;
  roleName?: string;
  cabangId?: number;
  perusahaanId?: number;
  status?: string;
  // Khusus role Mekanik
  spesialisasi?: string;
  pengalaman?: string;
  cabangIds?: number[];
};

type UserUpdatePayload = {
  nama?: string;
  noTelepon?: string;
  alamat?: string;
  status?: string;
  roleId?: number;
  roleName?: string;
  cabangId?: number | null;
  newPassword?: string;
  // Khusus role Mekanik
  spesialisasi?: string;
  pengalaman?: string;
  cabangIds?: number[];
};

const buildUserListQuery = (params?: {
  page?: number;
  perPage?: number;
  search?: string;
  status?: string;
  roleId?: number;
  cabangId?: number;
  perusahaanId?: number;
}) => {
  const q = new URLSearchParams();
  q.append('page', (params?.page ?? 1).toString());
  q.append('perPage', (params?.perPage ?? 10).toString());
  if (params?.search) q.append('search', params.search);
  if (params?.status) q.append('status', params.status);
  if (params?.roleId != null) q.append('roleId', params.roleId.toString());
  if (params?.cabangId != null) q.append('cabangId', params.cabangId.toString());
  if (params?.perusahaanId != null) q.append('perusahaanId', params.perusahaanId.toString());
  return q.toString();
};

export const userApi = {
  getById: (id: number) => apiClient.get(`/users/${id}`),

  updateSelf: (data: { nama?: string; noTelepon?: string; alamat?: string; currentPassword?: string; newPassword?: string }) =>
    apiClient.post('/users/me', data),

  admin: {
    getAll: (params?: {
      page?: number;
      perPage?: number;
      search?: string;
      status?: string;
      roleId?: number;
      cabangId?: number;
      perusahaanId?: number;
    }) => apiClient.get(`/users/admin?${buildUserListQuery(params)}`),
    create: (data: UserCreatePayload) => apiClient.post('/users/admin', data),
    update: (id: number, data: UserUpdatePayload) =>
      apiClient.post(`/users/admin/update-user/${id}`, data),
    deactivate: (id: number) => apiClient.delete(`/users/admin/${id}`),
  },

  cabang: {
    getAll: (params?: {
      page?: number;
      perPage?: number;
      search?: string;
      status?: string;
      roleId?: number;
      cabangId?: number;
    }) => apiClient.get(`/users/cabang?${buildUserListQuery(params)}`),
    create: (data: UserCreatePayload) => apiClient.post('/users/cabang', data),
    update: (id: number, data: UserUpdatePayload) =>
      apiClient.post(`/users/cabang/update-user/${id}`, data),
    deactivate: (id: number) => apiClient.delete(`/users/cabang/${id}`),
    lookup: (email: string) =>
      apiClient.get(`/users/cabang/lookup?email=${encodeURIComponent(email)}`),
  },
};

// User Perusahaan (membership) Management API — admin/superadmin scope
export const userPerusahaanApi = {
  getAll: (params?: {
    page?: number;
    perPage?: number;
    search?: string;
    status?: string;
    roleId?: number;
    cabangId?: number;
    perusahaanId?: number;
  }) => apiClient.get(`/user-perusahaan?${buildUserListQuery(params)}`),

  getById: (id: number) => apiClient.get(`/user-perusahaan/${id}`),

  create: (data: {
    userId: number;
    perusahaanId?: number;
    cabangId?: number;
    roleId: number;
    status?: string;
  }) => apiClient.post('/user-perusahaan', data),

  update: (id: number, data: {
    perusahaanId?: number;
    cabangId?: number;
    roleId?: number;
    status?: string;
  }) => apiClient.post(`/user-perusahaan/update-user-perusahaan/${id}`, data),

  delete: (id: number) => apiClient.delete(`/user-perusahaan/${id}`),
};

// Perusahaan Management API (admin-only)
type PerusahaanPayload = {
  nama_perusahaan: string;
  alamat?: string;
  telepon?: string;
  email_owner?: string;
  logo?: string;
};

export const perusahaanApi = {
  admin: {
    getAll: (params?: { page?: number; perPage?: number; search?: string }) => {
      const q = new URLSearchParams();
      q.append('page', (params?.page ?? 1).toString());
      q.append('perPage', (params?.perPage ?? 10).toString());
      if (params?.search) q.append('search', params.search);
      return apiClient.get(`/perusahaan/admin?${q.toString()}`);
    },
    create: (data: PerusahaanPayload) => apiClient.post('/perusahaan/admin', data),
    update: (id: number, data: PerusahaanPayload) =>
      apiClient.post(`/perusahaan/admin/update-perusahaan/${id}`, data),
    deactivate: (id: number) => apiClient.delete(`/perusahaan/admin/${id}`),
    activate: (id: number, data?: { lunasiTagihan?: boolean }) =>
      apiClient.post(`/perusahaan/admin/${id}/activate`, data ?? {}),
  },
  getById: (id: number) => apiClient.get(`/perusahaan/${id}`),
  generateKodeLangganan: (id: number) =>
    apiClient.post(`/perusahaan/${id}/generate-kode-langganan`, {}),
};

export const cabangApi = {
  admin: {
    getByPerusahaan: (perusahaanId: number) =>
      apiClient.get(`/cabang/admin?perusahaanId=${perusahaanId}`),
  },
  getAll: (params?: { page?: number; limit?: number; search?: string; status?: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('limit', (params?.limit ?? 10).toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status !== undefined && params.status !== '') queryParams.append('status', params.status);
    return apiClient.get(`/cabang?${queryParams.toString()}`);
  },
  getById: (id: number) => apiClient.get(`/cabang/${id}`),
  create: (data: { nama_cabang: string; alamat?: string; telepon?: string; latitude?: number | null; longitude?: number | null; radius_meter?: number | null }) =>
    apiClient.post('/cabang', data),
  update: (id: number, data: { nama_cabang: string; alamat?: string; telepon?: string; latitude?: number | null; longitude?: number | null; radius_meter?: number | null; status?: string }) =>
    apiClient.put(`/cabang/${id}`, data),
  delete: (id: number) => apiClient.delete(`/cabang/${id}`),
  search: (keyword: string) => apiClient.get(`/cabang/search?keyword=${encodeURIComponent(keyword)}`),
};

// Loket API
export const loketApi = {
  getAll: (params?: { page?: number; limit?: number; search?: string; cabangId?: number; status?: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('limit', (params?.limit ?? 10).toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.cabangId) queryParams.append('cabangId', params.cabangId.toString());
    if (params?.status !== undefined && params.status !== '') queryParams.append('status', params.status);
    return apiClient.get(`/loket?${queryParams.toString()}`);
  },
  getByCabang: (cabangId: number) => apiClient.get(`/loket/by-cabang/${cabangId}`),
  getById: (id: number) => apiClient.get(`/loket/${id}`),
  create: (data: { nomor_loket: number; cabang_id: number; status?: string }) =>
    apiClient.post('/loket', data),
  update: (id: number, data: { nomor_loket: number; cabang_id: number; status?: string }) =>
    apiClient.put(`/loket/${id}`, data),
  delete: (id: number) => apiClient.delete(`/loket/${id}`),
};

// Operasional API
export const operasionalApi = {
  getAll: () => apiClient.get('/operasional'),
  create: (data: { hari: number; namaHari?: string; isLibur?: boolean; jamBuka?: string; jamTutup?: string }) =>
    apiClient.post('/operasional', data),
  update: (data: { id: number; hari: number; namaHari?: string; isLibur?: boolean; jamBuka?: string; jamTutup?: string }) =>
    apiClient.post('/operasional/update-operasional', data),
  delete: (id: number) => apiClient.delete(`/operasional/${id}`),
};

export const operasionalKhususApi = {
  getAll: () => apiClient.get('/operasional-khusus'),
  create: (data: { tanggal: string; keterangan?: string }) =>
    apiClient.post('/operasional-khusus', data),
  update: (data: { id: number; tanggal: string; keterangan?: string }) =>
    apiClient.post('/operasional-khusus/update-operasional-khusus', data),
  delete: (id: number) => apiClient.delete(`/operasional-khusus/${id}`),
};

// Booking Service API
export const bookingApi = {
  getAll: (params?: { page?: number; perPage?: number; search?: string; status?: string; startDate?: string; endDate?: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    return apiClient.get<PaginationResponse<Booking>>(`/booking-service?${queryParams.toString()}`);
  },
  create: (data: {
    pelangganId: number;
    channelBooking: 'ONLINE' | 'WALK_IN' | 'FRONTDESK';
    tanggalBooking?: string;
    statusBooking?: string;
    waktuMulai?: string;
    waktuSelesai?: string;
    overrideWalkIn?: boolean;
    keluhan?: string;
    layananIds?: number[];
  }) => apiClient.post('/booking-service', data),
  update: (data: {
    id: number;
    antrianId?: number;
    tanggalBooking?: string;
    statusBooking?: string;
    channelBooking?: 'ONLINE' | 'WALK_IN' | 'FRONTDESK';
    waktuMulai?: string;
    waktuSelesai?: string;
    keluhan?: string;
    layananIds?: number[];
  }) => apiClient.post('/booking-service/update-booking', data),
  checkin: (id: number, latitude?: number, longitude?: number) =>
    apiClient.post(`/booking-service/${id}/checkin`, { latitude, longitude }),
  getDailySummary: (month: string) => apiClient.get<DailySummary[]>(`/booking-service/daily-summary?month=${month}`),
  lookupByKode: (kode: string) => apiClient.get<import('@/types/booking').BookingLookupResult>(`/booking-service/lookup-by-kode?kode=${encodeURIComponent(kode)}`),
  delete: (id: number) => apiClient.delete(`/booking-service/${id}`),
};

// Transaksi API
export const transaksiApi = {
  getAll: (params?: { page?: number; perPage?: number; search?: string; status?: string; startDate?: string; endDate?: string; statusPembayaran?: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    queryParams.append('search', params?.search ?? '');
    queryParams.append('status', params?.status ?? '');
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.statusPembayaran) queryParams.append('statusPembayaran', params.statusPembayaran);
    return apiClient.get<PaginationResponse<Transaksi>>(`/transaksi?${queryParams.toString()}`);
  },
  getById: (id: number) => apiClient.get(`/transaksi/${id}`),
  create: (data: {
    pelangganId: number;
    kendaraanId: number;
    layanan: Array<{ type?: string; layananId?: number; namaLayanan?: string; harga?: number; durasi?: number; mekanikIds: number[]; mekanikId?: number; keterangan?: string }>;
    barang: Array<{ barangId: number; jumlah: number }>;
    diskon?: number;
    diskonPersen?: number;
    metodeBayar: string;
    akunKasId?: number;
    nominalDiterima?: number;
    statusServis: string;
    statusPembayaran: string;
    keterangan?: string;
    kmServis?: number;
    keluhan?: string;
  }) => apiClient.post('/transaksi', data),
  update: (data: {
    id: number;
    pelangganId: number;
    kendaraanId: number;
    layanan: Array<{ type?: string; layananId?: number; namaLayanan?: string; harga?: number; durasi?: number; mekanikIds: number[]; mekanikId?: number; keterangan?: string }>;
    barang: Array<{ barangId: number; jumlah: number }>;
    diskon?: number;
    diskonPersen?: number;
    metodeBayar: string;
    akunKasId?: number;
    nominalDiterima?: number;
    statusServis: string;
    statusPembayaran: string;
    keterangan?: string;
    kmServis?: number;
    keluhan?: string;
  }) => apiClient.post('/transaksi/update-transaksi', data),
  // Update terbatas untuk mekanik: hanya status pengerjaan + keterangan.
  updateStatusServis: (id: number, data: { statusServis: string; keterangan: string }) =>
    apiClient.post(`/transaksi/${id}/status-servis`, data),
  delete: (id: number) => apiClient.delete(`/transaksi/${id}`),
  updateItems: (data: {
    id: number;
    barang?: Array<{
      id_barang: number;
      jumlah: number;
      harga: number;
    }>;
    layanan?: Array<{
      id_layanan: number;
      harga: number;
    }>;
    total_barang?: number;
    total_layanan?: number;
    diskon?: number;
    tarif_layanan?: number;
    nilai_tarif_layanan?: number;
    pajak?: number;
    nilai_pajak?: number;
    total_bayar?: number;
  }) => apiClient.post('/transaksi/update-items', data),

  updateBarang: (id: number, data: { id_barang: number, jumlah: number, harga_jual: number }) =>
    apiClient.post('/transaksi/update-barang', {
      id_transaksi: id,
      ...data
    }),

  updateLayanan: (id: number, data: { id_layanan: number, jumlah?: number, harga: number }) =>
    apiClient.post('/transaksi/update-layanan', {
      id_transaksi: id,
      ...data
    }),
  getRiwayatService: (params: { no_telepon: string; limit?: number }) => {
    const queryParams = new URLSearchParams({ no_telepon: params.no_telepon });
    if (params.limit) queryParams.append('limit', params.limit.toString());
    return apiClient.get(`/transaksi/riwayat-service?${queryParams.toString()}`);
  },

  searchByKode: (kode_transaksi: string) =>
    apiClient.get(`/transaksi/search-by-kode?kode_transaksi=${encodeURIComponent(kode_transaksi)}`),
};

// Pembelian API
export const pembelianApi = {
  getAll: (params?: { page?: number; perPage?: number; search?: string; status?: string; startDate?: string; endDate?: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    queryParams.append('search', params?.search ?? '');
    queryParams.append('status', params?.status ?? '');
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    return apiClient.get(`/pembelian?${queryParams.toString()}`);
  },
  create: (data: {
    supplierId: number;
    diskon?: number;
    statusPembelian: string;
    keterangan?: string;
    metodeBayar?: string;
    akunKasId?: number;
    jatuhTempo?: string;
    details: Array<{ barangId: number; jumlah: number }>;
    cicilan?: Array<{ tanggalCicil: string; nominal: number; metodePembayaran: string; akunKasId?: number; keterangan?: string }>;
  }) => apiClient.post('/pembelian', data),
  update: (data: {
    id: number;
    supplierId: number;
    diskon?: number;
    statusPembelian: string;
    keterangan?: string;
    metodeBayar?: string;
    akunKasId?: number;
    jatuhTempo?: string;
    details: Array<{ barangId: number; jumlah: number }>;
    cicilan?: Array<{ tanggalCicil: string; nominal: number; metodePembayaran: string; akunKasId?: number; keterangan?: string }>;
  }) => apiClient.post('/pembelian/update-pembelian', data),
  delete: (id: number | string) => apiClient.delete(`/pembelian/${id}`),
  getById: (id: number | string) => apiClient.get(`/pembelian/${id}`),
  tambahCicilan: (
    id: number | string,
    data: { tanggalCicil: string; nominal: number; metodePembayaran: string; akunKasId?: number; keterangan?: string },
  ) => apiClient.post(`/pembelian/${id}/cicilan`, data),
  batalCicilan: (
    id: number | string,
    cicilanId: number | string,
    data: { alasan: string },
  ) => apiClient.post(`/pembelian/${id}/cicilan/${cicilanId}/batal`, data),
  batalPembelian: (
    id: number | string,
    data: { alasan: string },
  ) => apiClient.post(`/pembelian/${id}/batal`, data),
};

// Penjualan API
export const penjualanApi = {
  getAll: (params?: { page?: number; perPage?: number; search?: string; status?: string; startDate?: string; endDate?: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    queryParams.append('search', params?.search ?? '');
    queryParams.append('status', params?.status ?? '');
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    return apiClient.get(`/penjualan?${queryParams.toString()}`);
  },
  create: (data: {
    pelangganId: number;
    diskonPersen?: number;
    diskon?: number;
    metodeBayar: string;
    akunKasId?: number;
    nominalDiterima: number;
    statusPembayaran: string;
    keterangan?: string;
    details: Array<{ barangId: number; jumlah: number }>;
  }) => apiClient.post('/penjualan', data),
  update: (data: {
    id: number;
    pelangganId: number;
    diskonPersen?: number;
    diskon?: number;
    metodeBayar: string;
    akunKasId?: number;
    nominalDiterima: number;
    statusPembayaran: string;
    keterangan?: string;
    details: Array<{ barangId: number | string; jumlah: number }>;
  }) => apiClient.post('/penjualan/update-penjualan', data),
  delete: (id: number | string) => apiClient.delete(`/penjualan/${id}`),
  getById: (id: number | string) => apiClient.get(`/penjualan/${id}`),
};

// Master Data API - Multi-tenant CRUD per perusahaan
export const masterDataApi = {
  // List paginated dengan filter
  getAll: (params?: { page?: number; perPage?: number; search?: string; key?: string; status?: number }) => {
    const qs = new URLSearchParams();
    qs.append('page', (params?.page ?? 1).toString());
    qs.append('perPage', (params?.perPage ?? 10).toString());
    if (params?.search) qs.append('search', params.search);
    if (params?.key) qs.append('key', params.key);
    if (params?.status !== undefined && params.status !== null) qs.append('status', params.status.toString());
    return apiClient.get(`/master-data/list?${qs.toString()}`);
  },
  // Distinct key list untuk autocomplete
  getKeys: () => apiClient.get('/master-data/keys'),
  // Legacy: list by key (active only) — dipakai utk dropdown sederhana
  getByKey: (key: string) => apiClient.get(`/master-data?key=${encodeURIComponent(key)}`),
  getSatuan: () => apiClient.get<Satuan[]>('/master-data/satuan'),
  create: (data: { key: string; value: string; description?: string; isActive?: number; perusahaanId?: number | null }) =>
    apiClient.post('/master-data', data),
  update: (data: { id: number; key: string; value: string; description?: string; isActive?: number; perusahaanId?: number | null }) =>
    apiClient.post('/master-data/update-master-data', data),
  delete: (id: number) => apiClient.delete(`/master-data/${id}`),
  getTarifConfig: () => apiClient.get('/master-data/tarif-config'),
};



// Dashboard API
export const dashboardApi = {
  getStats: () => apiClient.get('/dashboard'),
};

// API untuk retur
export const returApi = {
  getAll: (params?: {
    page?: number;
    perPage?: number;
    search?: string;
    status?: string;
    tipeRetur?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.perPage) queryParams.append('perPage', params.perPage.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.status) queryParams.append('status', params.status);
      if (params.tipeRetur) queryParams.append('tipeRetur', params.tipeRetur);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
    }
    return apiClient.get(`/retur?${queryParams.toString()}`);
  },

  getById: (id: number) => apiClient.get(`/retur/${id}`),

  delete: (id: number | string) => apiClient.delete(`/retur/${id}`),

  createNew: (data: {
    pelangganId?: number;
    supplierId?: number;
    tipeRetur: string;
    referensiId: number;
    referensiKode: string;
    alasanRetur: string;
    metodePengembalian: string;
    akunKasId?: number;
    potonganRetur?: number;
    persenPotonganRetur?: number;
    details: Array<{
      detailId: number;
      jumlah: number;
      alasanRetur?: string;
    }>;
  }) => {
    const payload = {
      pelangganId: 0,
      supplierId: 0,
      ...data
    };
    return apiClient.post('/retur', payload);
  },

  updateNew: (data: {
    id: number;
    alasanRetur: string;
    metodePengembalian: string;
    akunKasId?: number;
    potonganRetur?: number;
    persenPotonganRetur?: number;
    statusRetur: string;
    details: Array<{
      detailId: number;
      jumlah: number;
      alasanRetur?: string;
    }>;
  }) => apiClient.post('/retur/update-retur', data)
};

// API untuk refund
export const refundApi = {
  getAll: (params?: {
    page?: number;
    perPage?: number;
    search?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    queryParams.append('search', params?.search ?? '');
    queryParams.append('status', params?.status ?? '');
    queryParams.append('startDate', params?.startDate ?? '');
    queryParams.append('endDate', params?.endDate ?? '');
    return apiClient.get(`/refund?${queryParams.toString()}`);
  },

  getById: (id: number) => apiClient.get(`/refund/${id}`),

  create: (data: {
    transaksiId: number;
    metodePengembalian: string;
    akunKasId?: number;
    alasan: string;
    totalRefundPotongan: number;
    persenRefundPotongan?: number;
    details: Array<{
      tipeItem: 'barang' | 'layanan';
      transaksiBarangId: number;
      transaksiLayananId: number;
      qty: number;
    }>;
  }) => apiClient.post('/refund', data),

  update: (data: {
    id: number;
    metodePengembalian: string;
    akunKasId?: number;
    alasan: string;
    totalRefundPotongan: number;
    persenRefundPotongan?: number;
    status?: string;
    details: Array<{
      tipeItem: 'barang' | 'layanan';
      transaksiBarangId: number;
      transaksiLayananId: number;
      qty: number;
    }>;
  }) => apiClient.post('/refund/update-refund', data),

  delete: (id: number | string) => apiClient.delete(`/refund/${id}`),
};

// Tagihan Platform API
export const tagihanPlatformApi = {
  getAll: (params?: {
    page?: number;
    perPage?: number;
    status?: string;
    periodeBulan?: number;
    periodeTahun?: number;
    startDate?: string;
    endDate?: string;
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.periodeBulan) queryParams.append('periodeBulan', params.periodeBulan.toString());
    if (params?.periodeTahun) queryParams.append('periodeTahun', params.periodeTahun.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    return apiClient.get(`/tagihan-platform?${queryParams.toString()}`);
  },
  getById: (id: number) => apiClient.get(`/tagihan-platform/${id}`),
  update: (
    id: number,
    data: { status: string; ipaymuReference?: string; paymentUrl?: string }
  ) => apiClient.post(`/tagihan-platform/update-tagihan/${id}`, data),
  pay: (id: number, paymentMethod?: string) =>
    apiClient.post(`/tagihan-platform/${id}/pay`, paymentMethod ? { paymentMethod } : {}),
  delete: (id: number) => apiClient.delete(`/tagihan-platform/${id}`),
};

// Role API
export const roleApi = {
  getAll: (params?: { page?: number; perPage?: number; search?: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    if (params?.search) queryParams.append('search', params.search);
    return apiClient.get(`/role?${queryParams.toString()}`);
  },
  getAllCabang: (params?: { page?: number; perPage?: number; search?: string }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params?.page ?? 1).toString());
    queryParams.append('perPage', (params?.perPage ?? 10).toString());
    if (params?.search) queryParams.append('search', params.search);
    return apiClient.get(`/role/cabang?${queryParams.toString()}`);
  },
  // Role ringkas (id/name/description) untuk dropdown — tanpa permissions.
  getCabangOptions: () => apiClient.get('/role/cabang/options'),
  getById: (id: number) => apiClient.get(`/role/${id}`),
  create: (data: { name: string; description?: string; permissionIds: number[] }) =>
    apiClient.post('/role', data),
  update: (data: { id: number; name: string; description?: string; permissionIds: number[] }) =>
    apiClient.post('/role/update-role', data),
  delete: (id: number) => apiClient.delete(`/role/${id}`),
  // Override permission role bawaan untuk tenant aktif (Owner/SuperAdmin).
  saveOverride: (data: { roleId: number; permissionIds: number[] }) =>
    apiClient.post('/role/override', data),
  resetOverride: (roleId: number) => apiClient.delete(`/role/${roleId}/override`),
};

// Permission API
export const permissionApi = {
  getAll: () => apiClient.get('/permission'),
};

// Report API (daily / monthly / yearly aggregated reports)
export const reportApi = {
  getDaily: (date: string, cabangId: number) =>
    apiClient.get(`/report/daily?date=${encodeURIComponent(date)}&cabangId=${cabangId}`),
  getMonthly: (month: string, cabangId: number) =>
    apiClient.get(`/report/monthly?month=${encodeURIComponent(month)}&cabangId=${cabangId}`),
  getYearly: (year: number, cabangId: number) =>
    apiClient.get(`/report/yearly?year=${year}&cabangId=${cabangId}`),
  // Top barang terjual & top layanan (cabangId 0 = gabungan semua cabang)
  getTopBarang: (start: string, end: string, cabangId: number, limit: number) =>
    apiClient.get(`/report/top-barang?start=${start}&end=${end}&cabangId=${cabangId}&limit=${limit}`),
  getTopLayanan: (start: string, end: string, cabangId: number, limit: number) =>
    apiClient.get(`/report/top-layanan?start=${start}&end=${end}&cabangId=${cabangId}&limit=${limit}`),
  // Tren time-series satu seri dalam 1 panggilan (cabangId 0 = gabungan semua cabang)
  getTrend: (mode: 'bulanan' | 'tahunan', year: number, cabangId: number) =>
    apiClient.get(`/report/trend?mode=${mode}&year=${year}&cabangId=${cabangId}`),
  // Perbandingan antar cabang (time-series per cabang) untuk grafik
  compare: (mode: 'bulanan' | 'tahunan', year: number, cabangIds: number[]) =>
    apiClient.get(`/report/compare?mode=${mode}&year=${year}&cabangIds=${cabangIds.join(',')}`),
};

// Performa per role (cabangId 0 = gabungan semua cabang)
export interface PerformaMekanikRow {
  mekanikId: number;
  kodeMekanik: string;
  nama: string;
  jumlahLayanan: number;
  totalDurasiMenit: number;
  totalNilai: number;
}
export interface PerformaKasirRow {
  userId: number;
  nama: string;
  jumlahServis: number;
  nilaiServis: number;
  jumlahPenjualan: number;
  nilaiPenjualan: number;
  totalTransaksi: number;
  totalNilai: number;
}
export interface PerformaGudangRow {
  userId: number;
  nama: string;
  totalMasuk: number;
  totalKeluar: number;
  jumlahPenyesuaian: number;
}

export const performaApi = {
  getMekanik: (start: string, end: string, cabangId: number) =>
    apiClient.get<PerformaMekanikRow[]>(`/performa/mekanik?start=${start}&end=${end}&cabangId=${cabangId}`),
  getKasir: (start: string, end: string, cabangId: number) =>
    apiClient.get<PerformaKasirRow[]>(`/performa/kasir?start=${start}&end=${end}&cabangId=${cabangId}`),
  getGudang: (start: string, end: string, cabangId: number) =>
    apiClient.get<PerformaGudangRow[]>(`/performa/gudang?start=${start}&end=${end}&cabangId=${cabangId}`),
};

// ─── Kas & Bank / Payment Engine ─────────────────────────────────────────────

export const akunKasApi = {
  getAll: (params?: { page?: number; perPage?: number; search?: string; status?: string; tipe?: string }) => {
    const q = new URLSearchParams();
    q.append('page', (params?.page ?? 1).toString());
    q.append('perPage', (params?.perPage ?? 10).toString());
    if (params?.search) q.append('search', params.search);
    if (params?.status) q.append('status', params.status);
    if (params?.tipe) q.append('tipe', params.tipe);
    return apiClient.get<PaginationResponse<AkunKas>>(`/akun-kas?${q.toString()}`);
  },
  getAktif: () => apiClient.get<AkunKas[]>('/akun-kas/aktif'),
  create: (data: {
    kode: string; nama: string; tipe: string;
    nomorRekening?: string; atasNama?: string; saldoAwal?: number;
    isDefault?: boolean; isAktif?: boolean;
  }) => apiClient.post('/akun-kas', data),
  update: (data: {
    id: number; kode?: string; nama?: string; tipe?: string;
    nomorRekening?: string; atasNama?: string;
    isDefault?: boolean; isAktif?: boolean; status?: string;
  }) => apiClient.post('/akun-kas/update-akun-kas', data),
  delete: (id: number) => apiClient.delete(`/akun-kas/${id}`),
};

export const kategoriKasApi = {
  getAll: (params?: { page?: number; perPage?: number; search?: string; status?: string; arah?: string; sumber?: string }) => {
    const q = new URLSearchParams();
    q.append('page', (params?.page ?? 1).toString());
    q.append('perPage', (params?.perPage ?? 10).toString());
    if (params?.search) q.append('search', params.search);
    if (params?.status) q.append('status', params.status);
    if (params?.arah) q.append('arah', params.arah);
    if (params?.sumber) q.append('sumber', params.sumber);
    return apiClient.get<PaginationResponse<KategoriKas>>(`/kategori-kas?${q.toString()}`);
  },
  getAktif: (params?: { arah?: string; sumber?: string }) => {
    const q = new URLSearchParams();
    if (params?.arah) q.append('arah', params.arah);
    if (params?.sumber) q.append('sumber', params.sumber);
    return apiClient.get<KategoriKas[]>(`/kategori-kas/aktif?${q.toString()}`);
  },
  create: (data: { kode: string; nama: string; arah: string; isAktif?: boolean }) =>
    apiClient.post('/kategori-kas', data),
  update: (data: { id: number; kode?: string; nama?: string; arah?: string; isAktif?: boolean; status?: string }) =>
    apiClient.post('/kategori-kas/update-kategori-kas', data),
  delete: (id: number) => apiClient.delete(`/kategori-kas/${id}`),
};

export const kasApi = {
  kasMasuk: (data: {
    akunKasId: number; kategoriKasId: number; nominal: number;
    metode?: string; keterangan?: string; referensiEksternal?: string;
  }) => apiClient.post<Pembayaran>('/kas/kas-masuk', data),
  kasKeluar: (data: {
    akunKasId: number; kategoriKasId: number; nominal: number;
    metode?: string; keterangan?: string; referensiEksternal?: string;
  }) => apiClient.post<Pembayaran>('/kas/kas-keluar', data),
};

export const mutasiKasApi = {
  getAll: (params?: {
    page?: number; perPage?: number; akunKasId?: number; kategoriKasId?: number;
    arah?: string; referensiTipe?: string; status?: string; startDate?: string; endDate?: string;
  }) => {
    const q = new URLSearchParams();
    q.append('page', (params?.page ?? 1).toString());
    q.append('perPage', (params?.perPage ?? 10).toString());
    if (params?.akunKasId) q.append('akunKasId', params.akunKasId.toString());
    if (params?.kategoriKasId) q.append('kategoriKasId', params.kategoriKasId.toString());
    if (params?.arah) q.append('arah', params.arah);
    if (params?.referensiTipe) q.append('referensiTipe', params.referensiTipe);
    if (params?.status) q.append('status', params.status);
    if (params?.startDate) q.append('startDate', params.startDate);
    if (params?.endDate) q.append('endDate', params.endDate);
    return apiClient.get<PaginationResponse<MutasiKas>>(`/mutasi-kas?${q.toString()}`);
  },
  getSummary: (params?: { startDate?: string; endDate?: string }) => {
    const q = new URLSearchParams();
    if (params?.startDate) q.append('startDate', params.startDate);
    if (params?.endDate) q.append('endDate', params.endDate);
    return apiClient.get<ArusKasSummary>(`/mutasi-kas/summary?${q.toString()}`);
  },
};

export const pembayaranApi = {
  getAll: (params?: { page?: number; perPage?: number; referensiTipe?: string; status?: string; search?: string }) => {
    const q = new URLSearchParams();
    q.append('page', (params?.page ?? 1).toString());
    q.append('perPage', (params?.perPage ?? 10).toString());
    if (params?.referensiTipe) q.append('referensiTipe', params.referensiTipe);
    if (params?.status) q.append('status', params.status);
    if (params?.search) q.append('search', params.search);
    return apiClient.get<PaginationResponse<Pembayaran>>(`/pembayaran?${q.toString()}`);
  },
  getById: (id: number) => apiClient.get<Pembayaran>(`/pembayaran/${id}`),
  void: (id: number) => apiClient.post<Pembayaran>(`/pembayaran/${id}/void`, {}),
};

export default apiClient;